import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { unwrap } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { SCOUT_MODES, SCOUT_MODE_COPY, scoutRuleLabel } from '../../lib/scout-agent';
import type { ScoutMode, ScoutSettings } from '../../types/scout';
import { ScoutMetricsSection, ScoutShadowSection } from './ScoutShadowReport';

/**
 * Settings → AI & integrations → Scout (api#1079), the Sales & CRM agent.
 *
 * Copied from the Billing agent page (BillingAgentPage.tsx), section for
 * section:
 *   1. Mode — off / shadow / suggest. Admins and office staff read it; only an
 *      `admin` may change it, because PUT /scout/settings is STRICT admin (an
 *      owner and an elevated dispatcher are refused). Suggest asks first.
 *      Suggest is offered only once the PLATFORM allows it (`suggestAllowed`):
 *      before that the API answers 403, so the choice is drawn unavailable and
 *      says why rather than letting an admin find out from a toast.
 *   2. Checks — which rules are active (proven) and which are still learning.
 *      Read-only for everyone: proven is FieldIQ's decision, made on
 *      admin.field-iq.ai.
 *   3. Scorecard and 4. shadow report with labels — ScoutShadowReport.tsx.
 *
 * Cross-vertical, like Scout: no vertical gate. The workspace renders the
 * heading and tab strip; this page owns neither.
 */

export const SCOUT_SETTINGS_KEY = ['scout', 'settings'] as const;

const PANEL = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted';

function Pulse({ label }: { label: string }) {
  return <span role="status" aria-label={label} className="inline-block h-4 w-20 animate-pulse rounded bg-fieldiq-border" />;
}

export function useScoutSettings() {
  return useQuery({
    queryKey: SCOUT_SETTINGS_KEY,
    queryFn: async () => unwrap<ScoutSettings>((await api.scout.settings()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── 1. Mode ─────────────────────────────────────────────────────────────────

function ModeSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Strict admin, as the API's PUT is. isAdminRole would admit an owner the API refuses.
  const canChange = user?.role === 'admin';
  const [confirmSuggest, setConfirmSuggest] = useState(false);

  const query = useScoutSettings();
  const failure = queryLoadFailure(query);
  const current = query.data?.mode ?? null;
  const suggestAllowed = query.data?.suggestAllowed === true;

  const mutation = useMutation({
    mutationFn: (mode: ScoutMode) => api.scout.setMode(mode),
    onSuccess: (_res, mode) => {
      toast.success(`Scout set to ${SCOUT_MODE_COPY[mode].label}`);
      setConfirmSuggest(false);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to change the Scout mode'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SCOUT_SETTINGS_KEY });
      // The rep's brief reads the mode on every request.
      queryClient.invalidateQueries({ queryKey: ['scout', 'brief'] });
    },
  });

  /** Suggest cannot be CHOSEN until the platform allows it; a tenant already in suggest keeps it. */
  const unavailable = (mode: ScoutMode) => mode === 'suggest' && !suggestAllowed && current !== 'suggest';

  const choose = (mode: ScoutMode) => {
    if (!canChange || mode === current || mutation.isPending || unavailable(mode)) return;
    if (mode === 'suggest') setConfirmSuggest(true);
    else mutation.mutate(mode);
  };

  return (
    <section aria-labelledby="scout-mode" className={`${PANEL} border-l-4 border-l-fi-amber`} data-testid="scout-mode">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Scout</p>
        <h2 id="scout-mode" className="text-base font-semibold text-white">Mode</h2>
      </div>
      <div className="px-4 pb-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="Scout's mode" allowed="admins and office staff" />
        ) : query.isLoading || !current ? (
          <Pulse label="Loading the mode" />
        ) : (
          <div role="radiogroup" aria-label="Scout mode" aria-readonly={!canChange} className="grid gap-2 md:grid-cols-3">
            {SCOUT_MODES.map((mode) => {
              const selected = mode === current;
              const off = unavailable(mode);
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={off || undefined}
                  disabled={!canChange || mutation.isPending || off}
                  onClick={() => choose(mode)}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    selected ? 'border-fieldiq-accent bg-fieldiq-dark' : 'border-fieldiq-border'
                  } ${canChange && !off ? 'hover:border-fieldiq-accent' : 'cursor-default'} disabled:opacity-100`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-white">
                    {selected && <Check size={14} aria-hidden className="text-fieldiq-accent" />}
                    {SCOUT_MODE_COPY[mode].label}
                  </span>
                  <span className="mt-1 block text-xs text-fieldiq-muted">{SCOUT_MODE_COPY[mode].sentence}</span>
                  {off && (
                    <span className="mt-2 block text-xs font-semibold text-fieldiq-text" data-testid="suggest-unavailable">
                      Not available yet — FieldIQ turns it on once Scout has proven itself in Shadow.
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {current === 'suggest' && !suggestAllowed && (
          <p role="note" data-testid="suggest-withdrawn"
            className="mt-3 flex items-start gap-2 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text">
            <Info size={14} aria-hidden className="mt-0.5 shrink-0 text-fieldiq-muted" />
            FieldIQ has paused Suggest for every company, so reps see nothing until it is back on. Admins and office staff still do.
          </p>
        )}
      </div>
      {!canChange && !failure && (
        <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">Only an admin can change this.</p>
      )}

      <ConfirmModal
        isOpen={confirmSuggest}
        title="Show Scout to your sales reps?"
        body={<p>Reps will get Scout's brief, nudges and meeting logging for every proven check. Checks still learning stay with admins and office staff.</p>}
        confirmLabel="Switch to Suggest"
        confirmVariant="primary"
        loading={mutation.isPending}
        onConfirm={() => mutation.mutate('suggest')}
        onCancel={() => setConfirmSuggest(false)}
      />
    </section>
  );
}

// ─── 2. Checks ───────────────────────────────────────────────────────────────

/** The two values of `proven`, so the map is exhaustive. */
const RULE_STATUS_BADGE = {
  active: { label: 'Active', fill: 'bg-fi-signal-green' },
  learning: { label: 'Still learning', fill: 'bg-fi-signal-slate' },
} as const;

function ChecksSection() {
  const query = useScoutSettings();
  const failure = queryLoadFailure(query);
  const rules = query.data?.rules;

  return (
    <section aria-labelledby="scout-checks" className={PANEL} data-testid="scout-checks">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Checks</p>
        <h2 id="scout-checks" className="text-base font-semibold text-white">What Scout does</h2>
      </div>
      {failure ? (
        <div className="px-4 pb-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="Scout's checks" allowed="admins and office staff" />
        </div>
      ) : query.isLoading || !rules ? (
        <div className="px-4 pb-4"><Pulse label="Loading the checks" /></div>
      ) : (
        <>
          <ul className="divide-y divide-fieldiq-border border-t border-fieldiq-border">
            {rules.map((r) => {
              const badge = RULE_STATUS_BADGE[r.proven ? 'active' : 'learning'];
              return (
                <li key={`${r.rule}@${r.ruleVersion}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`scout-rule-${r.rule}`}>
                  <span className="text-fieldiq-text">{scoutRuleLabel(r.rule)}</span>
                  <span className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${badge.fill}`}>
                    {badge.label}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
            FieldIQ switches a check on for reps once it's proven accurate. Until then only admins and office staff see it.
          </p>
        </>
      )}
    </section>
  );
}

// ─── the page ────────────────────────────────────────────────────────────────

export default function ScoutAgentPage() {
  return (
    <div className="min-h-full">
      <div className="px-6 py-4 space-y-4 pb-[var(--bottom-chrome)]">
        <p className="text-xs text-fieldiq-muted">
          Scout helps your sales reps: it logs a meeting from one spoken sentence, briefs them before a visit, and lists the follow-ups and quiet customers to chase each morning.
        </p>
        <ModeSection />
        <ChecksSection />
        <ScoutMetricsSection />
        <ScoutShadowSection />
      </div>
    </div>
  );
}
